<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';
import draggable from 'vuedraggable';
import TaskListItem from './TaskListItem.vue';
import TaskInlineCreate from './TaskInlineCreate.vue';

const props = defineProps({
  allowDrag: { type: Boolean, default: true },
  showInlineCreate: { type: Boolean, default: false },
  selectionMode: { type: Boolean, default: false },
  selectedIds: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'taskOpen',
  'taskComplete',
  'taskUpdated',
  'inlineCreateDone',
  'dragReorder',
  'taskSelected',
  'taskOpenWithWarning',
]);

const { t } = useI18n();
const store = useStore();
const { updateTask } = useTaskActions();

const tasks = computed(() => store.getters['tasks/allTasks']);
const isLoading = computed(() => store.getters['tasks/uiFlags'].isLoading);

// lista local para drag — sincronizada com o store
const localTasks = ref([]);
const dragging = ref(false);

// sincroniza quando o store muda (mas não enquanto arrastar)
watch(
  tasks,
  val => {
    if (!dragging.value) localTasks.value = [...val];
  },
  { immediate: true }
);

async function onDragEnd() {
  const snapshot = [...localTasks.value];
  // avisa o pai antes de salvar — muda sort para personalizado imediatamente
  emit('dragReorder', snapshot);
  const updates = snapshot.map((task, index) =>
    updateTask({ taskId: task.id, position: index })
  );
  await Promise.all(updates);
  localTasks.value = snapshot;
  dragging.value = false;
  emit('taskUpdated');
}
</script>

<template>
  <div class="flex flex-col">
    <div v-if="isLoading" class="flex justify-center items-center py-10">
      <span class="i-lucide-loader size-6 text-n-slate-9 animate-spin" />
    </div>

    <div
      v-else-if="localTasks.length === 0"
      class="flex flex-col items-center justify-center py-16 gap-3 text-n-slate-10"
    >
      <span class="i-lucide-check-square size-10" />
      <p class="text-sm">{{ t('TASKS.EMPTY_STATE') }}</p>
    </div>

    <TaskInlineCreate
      v-if="props.showInlineCreate"
      @created="
        emit('inlineCreateDone');
        emit('taskUpdated');
      "
      @cancel="emit('inlineCreateDone')"
    />

    <draggable
      v-model="localTasks"
      item-key="id"
      :handle="props.allowDrag ? '.task-drag-item' : '.drag-handle-disabled'"
      ghost-class="opacity-40"
      drag-class="shadow-xl"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <template #item="{ element: task }">
        <div>
          <TaskListItem
            :task="task"
            :selection-mode="props.selectionMode"
            :selected="props.selectedIds.includes(task.id)"
            @open="emit('taskOpen', task)"
            @complete="emit('taskComplete', task)"
            @open-with-warning="emit('taskOpenWithWarning', task)"
            @updated="emit('taskUpdated')"
            @select="id => emit('taskSelected', id)"
          />
        </div>
      </template>
    </draggable>
  </div>
</template>
